import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { Input } from '@components/core-ui/form-elements/Input';
import { buildSpeakerLabelOverrides } from '@helpers/buildSpeakerLabelOverrides';
import { getSpeakerLabelValidationError } from '@helpers/getSpeakerLabelValidationError';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useEffect, useMemo, useState } from 'react';
import { TProps } from './types/TProps';

const buildEditedLabelsState = (labels: string[]) => Object.fromEntries(labels.map((label) => [label, label]));

export const RegenerateWithLayoutModal = ({ onCancel, onContinue, speakerLabels }: TProps) => {
  const [editEnabled, setEditEnabled] = useState(false);
  const [editedLabels, setEditedLabels] = useState<Record<string, string>>(() => buildEditedLabelsState(speakerLabels));

  useEffect(() => {
    setEditedLabels(buildEditedLabelsState(speakerLabels));
    setEditEnabled(false);
  }, [speakerLabels]);

  const hasEmptyEditedLabel = useMemo(() => {
    if (!editEnabled) {
      return false;
    }
    return speakerLabels.some((label) => !(editedLabels[label] ?? '').trim());
  }, [editEnabled, editedLabels, speakerLabels]);

  const speakerLabelValidationErrors = useMemo(() => {
    if (!editEnabled) {
      return {} as Record<string, string | null>;
    }

    return Object.fromEntries(
      speakerLabels.map((label) => [label, getSpeakerLabelValidationError(editedLabels[label] ?? '')]),
    );
  }, [editEnabled, editedLabels, speakerLabels]);

  const hasInvalidEditedLabel = useMemo(
    () => editEnabled && speakerLabels.some((label) => speakerLabelValidationErrors[label]),
    [editEnabled, speakerLabelValidationErrors, speakerLabels],
  );

  const submit = () => {
    if (hasEmptyEditedLabel || hasInvalidEditedLabel) {
      return;
    }

    onContinue(buildSpeakerLabelOverrides(speakerLabels, editedLabels, editEnabled));
  };

  return (
    <ModalWrapper width="487px">
      <ModalTitle>Change Cloud Layout?</ModalTitle>

      <div className="flex flex-col gap-3 text-base leading-6 text-[var(--color-text)]">
        <p className="m-0">
          Applying a new Cloud Layout will regenerate this job and create a new version. As part of this process,{' '}
          <strong>
            all existing job versions and any edits made within those versions will be permanently removed
          </strong>{' '}
          and cannot be recovered.
        </p>
        <p className="m-0">
          Only continue if you want to replace the current Cloud Layout and start with a newly generated version.
        </p>
      </div>

      {speakerLabels.length > 0 ? (
        <div className="border-t border-[var(--color-border)] pt-5">
          <p className="m-0 text-base leading-6 text-[var(--color-text)]">
            You can rename speaker labels before regenerating the transcript with the selected layout.
          </p>

          <div className="mt-4">
            <CheckBox
              name="editSpeakerLabels"
              checked={editEnabled}
              onChange={setEditEnabled}
              label="Edit speaker labels"
            />
          </div>

          <div className="mt-4 flex flex-col gap-3">
            {speakerLabels.map((label) => (
              <Input
                key={label}
                label={`Speaker ${label}`}
                value={editedLabels[label] ?? label}
                disabled={!editEnabled}
                error={editEnabled ? Boolean(speakerLabelValidationErrors[label]) : false}
                onChange={(event) =>
                  setEditedLabels((current) => ({
                    ...current,
                    [label]: event.target.value,
                  }))
                }
              />
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-5 flex justify-end gap-2.5">
        <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
        <Button onClick={submit} disabled={hasEmptyEditedLabel || hasInvalidEditedLabel}>
          Continue
        </Button>
      </div>
    </ModalWrapper>
  );
};
